import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Copy,
  Download,
  GripVertical,
  Info,
  ListOrdered,
  Pencil,
  PhoneOff,
  RotateCcw,
  UserX,
  X,
} from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { EndCallDialog } from "@/components/flow-player/EndCallDialog";
import { StepOrderDialog } from "@/components/flow-player/StepOrderDialog";
import { GuidePanel } from "@/components/guides/GuidePanel";
import { PageSkeleton } from "@/components/page-skeleton";
import { StatusPill } from "@/components/status-pill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateCallLog,
  useFlowGraph,
  useFlowPersonalization,
  useFlowStepOrder,
  useResetFlowStepOrder,
  useResetNodePersonalization,
  useSaveFlowStepOrder,
  useSaveNodePersonalization,
} from "@/hooks/api/flows";
import { useDragReorder } from "@/hooks/useDragReorder";
import { apiErrorMessage, isAxiosError } from "@/lib/api";
import { resolveBilingual, type ResolvedText } from "@/lib/bilingual";
import {
  CLIENT_INFO_PLACEHOLDER,
  EMPTY_CLIENT_INFO,
  isClientInfoComplete,
  type ClientInfoValue,
} from "@/lib/clientInfo";
import { applyPersonalOrder } from "@/lib/reorder";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import type {
  CallEndReason,
  CallLogStepInput,
  FlowEdge,
  FlowNode,
  LanguagePreference,
  NodePersonalization,
} from "@/types/api";

/** Zips `breadcrumb` (nodes visited) with `visitedEdges` (the edge chosen
 * at each of those), `freeTextEntries` (what the CSR typed, for a Text
 * Entry step — `null` everywhere else), and `clientInfoEntries` (the
 * caller's details, for a Client Information step — `null` everywhere
 * else) into the {node_id, edge_id, free_text?, client_info?} tuples
 * CallLog.steps expects — only ids (and, for those two step types, the
 * actual captured data) cross the wire, see CallLogWriteInput. Shared by
 * both places a call gets logged (auto-complete and manual End call) so
 * they can't drift out of sync on what a "step" is. */
function buildCallLogSteps(
  breadcrumb: string[],
  visitedEdges: FlowEdge[],
  freeTextEntries: (string | null)[],
  clientInfoEntries: (ClientInfoValue | null)[]
): CallLogStepInput[] {
  return breadcrumb.flatMap((nodeId, i) => {
    const edge = visitedEdges[i];
    if (!edge) return [];
    const freeText = freeTextEntries[i];
    const clientInfo = clientInfoEntries[i];
    return [
      {
        node_id: nodeId,
        edge_id: edge.id,
        ...(freeText != null ? { free_text: freeText } : {}),
        ...(clientInfo != null
          ? {
              client_info: {
                first_name: clientInfo.first_name,
                last_name: clientInfo.last_name,
                date_of_birth: clientInfo.date_of_birth || null,
                email: clientInfo.email,
                phone_number: clientInfo.phone_number,
                declined_to_share: clientInfo.declined_to_share,
              },
            }
          : {}),
      },
    ];
  });
}

/** A finished call's compiled answers — captured the instant the flow hits
 * its END node, right before the player resets for the next call. Kept
 * around (not tied to the live breadcrumb/currentNode, which get cleared
 * on reset) so Staff can still copy/download it after the flow has already
 * moved on. */
interface CallSummarySnapshot {
  flowTitle: string;
  completedAt: number;
  entries: { question: string; answer: string }[];
  // Kept out of `entries` on purpose — see the layout effect below and
  // ClientInfoSummary — so the caller's personal details render in their
  // own section rather than sitting inline with routine branch choices.
  clientInfo: ClientInfoValue | null;
  outcome: string;
}

/** Small keycap-style badge for a keyboard-shortcut hint next to the
 * button it triggers — self-documenting, no separate legend needed. */
function KeyHint({ children }: { children: ReactNode }) {
  return (
    <kbd
      aria-hidden="true"
      className="inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded border border-slate-300 bg-surface-alt px-1 font-sans text-[10px] font-semibold not-italic text-slate-500"
    >
      {children}
    </kbd>
  );
}

/** Renders a resolved bilingual text block. When both languages are shown
 * (preference = BOTH), each gets a small EN/ES tag — this text can be read
 * live mid-call, so translations are never hidden behind a hover/tooltip. */
function BilingualBlock({ text, className }: { text: ResolvedText; className: string }) {
  if (!text.secondary) {
    return <p className={className}>{text.primary}</p>;
  }
  return (
    <div className="space-y-3">
      <div>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          EN
        </span>
        <p className={className}>{text.primary}</p>
      </div>
      <div className="border-t border-slate-200 pt-3">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          ES
        </span>
        <p className={className}>{text.secondary}</p>
      </div>
    </div>
  );
}

/**
 * The spiel, editable in place. A Staff member's own wording (if any)
 * always wins over the admin's original, per field — an untouched language
 * just falls through to the script's version, same fallback shape
 * `resolveBilingual` already uses for EN/ES. Never touches `internal_note`
 * or the step title — personalization is scoped to what's actually read
 * aloud, see StaffNodeOverride. Keyed by node id from the caller so
 * switching steps always mounts fresh (no stale draft carried over).
 */
function PersonalizedSpiel({
  node,
  override,
  languagePreference,
  onSave,
  onReset,
  saving,
  resetting,
}: {
  node: FlowNode;
  override: NodePersonalization | undefined;
  languagePreference: LanguagePreference;
  onSave: (input: { spiel_text: string; spiel_text_es: string }) => Promise<void>;
  onReset: () => void;
  saving: boolean;
  resetting: boolean;
}) {
  const { t } = useTranslation();
  // Wording-specific, not "does an override row exist at all" — `override`
  // is also truthy for an edge_order-only personalization (see the
  // branch-reorder controls below), which must not paint this component's
  // "Personalized"/"Reset to script" wording UI when only the branch order
  // was ever touched.
  const isPersonalized = !!(override?.spiel_text.trim() || override?.spiel_text_es.trim());
  const effectiveEn = override?.spiel_text.trim() ? override.spiel_text : node.spiel_text;
  const effectiveEs = override?.spiel_text_es.trim() ? override.spiel_text_es : node.spiel_text_es;

  const [editing, setEditing] = useState(false);
  const [draftEn, setDraftEn] = useState(effectiveEn);
  const [draftEs, setDraftEs] = useState(effectiveEs);

  const spiel = resolveBilingual(languagePreference, {
    en: effectiveEn || node.title,
    es: effectiveEs || node.title_es,
  });

  const showEn = languagePreference !== "ES";
  const showEs = languagePreference !== "EN";

  // Seed each field from what the editor actually shows for it — the
  // blended "effective" value for a language the user can see and edit
  // here, but the *raw* override for one hidden by their language
  // preference. Seeding a hidden field from the effective value would
  // silently resubmit the admin's fallback text as if the user had
  // personalized it, permanently freezing a copy of it into the override
  // — so a future edit to the script's original wording would stop
  // reaching them in that language even though they never touched it.
  const startEditing = () => {
    setDraftEn(showEn ? effectiveEn : override?.spiel_text ?? "");
    setDraftEs(showEs ? effectiveEs : override?.spiel_text_es ?? "");
    setEditing(true);
  };

  const save = async () => {
    try {
      await onSave({ spiel_text: draftEn.trim(), spiel_text_es: draftEs.trim() });
      setEditing(false);
    } catch {
      // Stay in edit mode on failure so the draft isn't lost — the caller
      // already surfaced a toast with the reason; let the user retry.
    }
  };

  if (editing) {
    return (
      <div className="mt-3 max-w-prose space-y-3">
        {showEn && (
          <div className="space-y-1">
            <Label className="text-xs font-normal text-slate-500">
              {t("flowPlayer.personalizeEnglishLabel")}
            </Label>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- opening the
             * editor is itself the user's explicit request to type */}
            <Textarea
              rows={4}
              autoFocus
              value={draftEn}
              onChange={(e) => setDraftEn(e.target.value)}
              className="text-base"
            />
          </div>
        )}
        {showEs && (
          <div className="space-y-1">
            <Label className="text-xs font-normal text-slate-500">
              {t("flowPlayer.personalizeSpanishLabel")}
            </Label>
            <Textarea
              rows={4}
              value={draftEs}
              onChange={(e) => setDraftEs(e.target.value)}
              className="text-base"
            />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={save} loading={saving}>
            {t("flowPlayer.save")}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
            {t("flowPlayer.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 max-w-prose">
      <BilingualBlock text={spiel} className="text-xl leading-relaxed text-slate-900 sm:text-2xl" />
      {spiel.isFallback && (
        <p className="mt-1 text-xs italic text-slate-400">{t("flowPlayer.spanishNotAvailable")}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {isPersonalized && <Badge tone="info">{t("flowPlayer.personalizedBadge")}</Badge>}
        <button
          type="button"
          onClick={startEditing}
          className="flex cursor-pointer items-center gap-1 text-xs font-medium text-slate-500 hover:text-primary hover:underline"
        >
          <Pencil className="h-3 w-3" aria-hidden="true" />
          {isPersonalized ? t("flowPlayer.editPersonalization") : t("flowPlayer.personalizeThis")}
        </button>
        {isPersonalized && (
          <button
            type="button"
            onClick={onReset}
            disabled={resetting}
            className="flex cursor-pointer items-center gap-1 text-xs font-medium text-slate-500 hover:text-danger hover:underline disabled:pointer-events-none disabled:opacity-50"
          >
            <RotateCcw className="h-3 w-3" aria-hidden="true" />
            {t("flowPlayer.resetToScript")}
          </button>
        )}
      </div>
    </div>
  );
}

/** A Text Entry step's answer box — the CSR types whatever the customer
 * said (a name, a callback number, free-form notes) instead of picking
 * from predefined branches. Continue stays disabled until they've typed
 * something, matching how Selection/Question steps already require a
 * choice before advancing — see the "Required" call in
 * FormBasedFlowBuilder's STEP_TYPE_OPTIONS. Mounted with `key={node.id}`
 * by the caller so the draft always starts blank on a fresh step, the same
 * pattern PersonalizedSpiel uses. */
function FreeTextAnswer({ onSubmit }: { onSubmit: (text: string) => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const trimmed = value.trim();

  return (
    <div className="space-y-2">
      <Textarea
        rows={3}
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t("flowPlayer.freeTextPlaceholder")}
        maxLength={500}
        className="text-base"
      />
      <Button onClick={() => onSubmit(trimmed)} disabled={!trimmed}>
        {t("flowPlayer.continueLabel")}
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

/** A Client Information step's entry form — name, last name, date of
 * birth, email, phone. Continue stays disabled until name/last
 * name/DOB/phone are filled in (email is always optional — see
 * isClientInfoComplete); a caller who won't share their real details
 * doesn't have to block the rest of the call, though — the "client
 * declined" button fills the required fields with an obvious placeholder
 * (see CLIENT_INFO_PLACEHOLDER) instead of leaving Staff stuck here.
 * Mounted with `key={node.id}` by the caller, same as FreeTextAnswer, so
 * the draft always starts blank on a fresh step. */
function ClientInfoAnswer({ onSubmit }: { onSubmit: (value: ClientInfoValue) => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState<ClientInfoValue>(EMPTY_CLIENT_INFO);
  const set = (patch: Partial<ClientInfoValue>) => setValue((v) => ({ ...v, ...patch }));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label className="text-xs font-normal text-slate-500">
            {t("flowPlayer.clientInfoFirstName")}
          </Label>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- opening this
           * step is itself the CSR's cue to start typing, same reasoning
           * as FreeTextAnswer's autoFocus above. */}
          <Input
            autoFocus
            value={value.first_name}
            onChange={(e) => set({ first_name: e.target.value })}
            maxLength={150}
            className="text-base"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-normal text-slate-500">
            {t("flowPlayer.clientInfoLastName")}
          </Label>
          <Input
            value={value.last_name}
            onChange={(e) => set({ last_name: e.target.value })}
            maxLength={150}
            className="text-base"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-normal text-slate-500">
            {t("flowPlayer.clientInfoDob")}
          </Label>
          <Input
            type="date"
            value={value.date_of_birth}
            onChange={(e) => set({ date_of_birth: e.target.value })}
            className="text-base"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-normal text-slate-500">
            {t("flowPlayer.clientInfoPhone")}
          </Label>
          <Input
            type="tel"
            value={value.phone_number}
            onChange={(e) => set({ phone_number: e.target.value })}
            maxLength={30}
            className="text-base"
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs font-normal text-slate-500">
            {t("flowPlayer.clientInfoEmail")}
          </Label>
          <Input
            type="email"
            value={value.email}
            onChange={(e) => set({ email: e.target.value })}
            maxLength={254}
            className="text-base"
          />
        </div>
      </div>
      {/* Both real buttons, side by side, so neither reads as attached to
       * or gated by the other — a plain text link next to Continue's
       * arrow previously read as if the arrow pointed at it, and its lack
       * of a button outline made it easy to miss as clickable at all. The
       * exceptional path (declined) sits before the primary one
       * (Continue), same left-to-right order as a Cancel/Confirm pair. */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setValue(CLIENT_INFO_PLACEHOLDER)}
        >
          <UserX className="h-4 w-4" aria-hidden="true" />
          {t("flowPlayer.clientInfoDeclined")}
        </Button>
        <Button onClick={() => onSubmit(value)} disabled={!isClientInfoComplete(value)}>
          {t("flowPlayer.continueLabel")}
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

/** The caller's details on the post-call summary card — a separate box
 * from the step-by-step `entries` list (see CallSummarySnapshot), not
 * just another Q&A line, so it reads as the distinct thing it is. */
function ClientInfoSummary({ info }: { info: ClientInfoValue }) {
  const { t } = useTranslation();
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-surface-alt p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">{t("flowPlayer.clientInfoHeading")}</span>
        {info.declined_to_share && (
          <Badge tone="warning">{t("flowPlayer.clientInfoDeclinedBadge")}</Badge>
        )}
      </div>
      <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-slate-500">{t("flowPlayer.clientInfoFirstName")}</dt>
          <dd className="text-slate-800">{info.first_name || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">{t("flowPlayer.clientInfoLastName")}</dt>
          <dd className="text-slate-800">{info.last_name || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">{t("flowPlayer.clientInfoDob")}</dt>
          <dd className="text-slate-800">{info.date_of_birth || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">{t("flowPlayer.clientInfoPhone")}</dt>
          <dd className="text-slate-800">{info.phone_number || "—"}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-xs text-slate-500">{t("flowPlayer.clientInfoEmail")}</dt>
          <dd className="text-slate-800">{info.email || "—"}</dd>
        </div>
      </dl>
    </div>
  );
}

/** The step-by-step Q&A list + client info block on the end-of-call summary
 * card — one labeled block per entry, meant to be read closely (and
 * copied/downloaded) after a call wraps up. The call *in progress* uses a
 * much more compact style instead — see LiveNotes below — since that one
 * has to sit alongside the script without competing with it for
 * attention. `emptyMessage` renders in place of the list when there are no
 * entries. */
function SummaryEntries({
  entries,
  clientInfo,
  emptyMessage,
}: {
  entries: { question: string; answer: string }[];
  clientInfo: ClientInfoValue | null;
  emptyMessage?: string;
}) {
  return (
    <>
      {entries.length === 0
        ? emptyMessage && <p className="mt-3 text-sm text-slate-500">{emptyMessage}</p>
        : (
          <ol className="mt-3 space-y-3 text-sm">
            {entries.map((entry, i) => (
              <li key={i} className="border-l-2 border-slate-200 pl-3">
                <div className="font-medium text-slate-900">{entry.question}</div>
                <div className="text-slate-600">→ {entry.answer}</div>
              </li>
            ))}
          </ol>
        )}
      {clientInfo && <ClientInfoSummary info={clientInfo} />}
    </>
  );
}

/** A running, notepad-style line of what's been captured in the call
 * currently in progress — one short "label: value" fragment per step,
 * comma-separated in a single block of text instead of SummaryEntries'
 * one-block-per-entry layout. Modeled on how a CSR's own call notes
 * actually read in practice: compact abbreviated fragments on a line,
 * not a fully-labeled field for every single value. Lives in the sidebar
 * (see the layout below) precisely so it reads as a notepad off to the
 * side, not another part of the script. Renders nothing once there's
 * nothing captured yet. */
function LiveNotes({
  entries,
  clientInfo,
}: {
  entries: { question: string; answer: string }[];
  clientInfo: ClientInfoValue | null;
}) {
  const { t } = useTranslation();

  const clientInfoFragment = clientInfo
    ? clientInfo.declined_to_share
      ? t("flowPlayer.clientInfoDeclinedBadge")
      : [
          [clientInfo.first_name, clientInfo.last_name].filter(Boolean).join(" "),
          clientInfo.date_of_birth &&
            `${t("flowPlayer.liveNotesDobAbbrev")} ${clientInfo.date_of_birth}`,
          clientInfo.phone_number,
          clientInfo.email,
        ]
          .filter(Boolean)
          .join(", ")
    : null;

  const fragments = [
    ...entries.map((e) => `${e.question}: ${e.answer}`),
    ...(clientInfoFragment ? [clientInfoFragment] : []),
  ];
  if (fragments.length === 0) return null;

  return (
    <div className="app-card shrink-0 p-3">
      <h2 className="m-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {t("flowPlayer.capturedSoFarHeading")}
      </h2>
      <p className="mt-1.5 font-mono text-xs leading-relaxed text-slate-700">
        {fragments.join(",  ")}
      </p>
    </div>
  );
}

export default function FlowPlayerPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const graphQuery = useFlowGraph(id);
  // Called unconditionally alongside the graph query (rules of hooks) even
  // though the UI below only needs it once the graph has loaded.
  const personalizationQuery = useFlowPersonalization(id);
  const savePersonalization = useSaveNodePersonalization(id);
  const resetPersonalization = useResetNodePersonalization(id);
  // This Staff member's own preferred order for the whole flow's steps —
  // see StepOrderDialog. Distinct from personalizationQuery above, which
  // is per-node (wording + one node's own branch order).
  const stepOrderQuery = useFlowStepOrder(id);
  const saveStepOrder = useSaveFlowStepOrder(id);
  const resetStepOrder = useResetFlowStepOrder(id);
  const createCallLog = useCreateCallLog(id);
  const languagePreference: LanguagePreference =
    useAuthStore((s) => s.user?.language_preference) ?? "BOTH";

  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<string[]>([]);
  // The edge chosen at each breadcrumb step, same order/length as
  // `breadcrumb` — together they're the "answers" that build the
  // end-of-call summary (which branch was picked at each step).
  const [visitedEdges, setVisitedEdges] = useState<FlowEdge[]>([]);
  // What the CSR typed at each breadcrumb step, same order/length as
  // `breadcrumb` — `null` for every step that wasn't a Text Entry step.
  // Kept as its own parallel array (rather than folded into visitedEdges)
  // because the edge at a Text Entry step is just structural routing to
  // the next step; the actual answer is whatever the CSR typed, which
  // varies per call and has nowhere else to live.
  const [freeTextEntries, setFreeTextEntries] = useState<(string | null)[]>([]);
  // The caller's details captured at each breadcrumb step, same
  // order/length as `breadcrumb` — `null` for every step that wasn't a
  // Client Information step. Same reasoning as freeTextEntries above: its
  // own parallel array because this data has nowhere else to live.
  const [clientInfoEntries, setClientInfoEntries] = useState<(ClientInfoValue | null)[]>([]);
  // The most recently completed call's summary — survives the reset below
  // so it stays available to copy/download while the next call is already
  // underway. `null` once dismissed or before any call has finished.
  const [lastCallSummary, setLastCallSummary] = useState<CallSummarySnapshot | null>(null);
  // When the call currently in progress began — reset whenever a call
  // ends (normally or early) and the player resets for the next one.
  // Feeds CallLog.started_at, purely for reporting (how long a call ran).
  const [callStartedAt, setCallStartedAt] = useState(() => Date.now());
  const [endCallDialogOpen, setEndCallDialogOpen] = useState(false);
  const [stepOrderDialogOpen, setStepOrderDialogOpen] = useState(false);

  const startNode = useMemo(
    () => graphQuery.data?.nodes.find((n) => n.node_type === "START") ?? null,
    [graphQuery.data]
  );
  const firstRealNodeId = useMemo(() => {
    if (!startNode || !graphQuery.data) return startNode?.id ?? null;
    const startEdges = graphQuery.data.edges.filter((e) => e.source_node === startNode.id);
    // A plain pass-through START (exactly one outgoing edge, the normal
    // case) skips straight to the real first step. A START that itself
    // branches — e.g. a Selection step saved as the flow's first step —
    // must stay put so all of its options are reachable, not just
    // whichever edge happened to come first.
    const scriptDefault = startEdges.length === 1 ? startEdges[0].target_node : startNode.id;
    // A Staff member's own step order (see StepOrderDialog) can override
    // where a fresh call starts — its own first entry. Falls back to the
    // script's own default start unless that entry still names a real,
    // orderable step: still exists, still isn't START/END (an admin could
    // have retyped this exact node id since it was saved — node identity
    // survives a graph re-save, see FlowViewSet.graph), and still has
    // somewhere to go (an admin could have stripped its outgoing edges) —
    // so landing here can never immediately strand the CSR with no branch
    // configured before they've even started. Each step's own routing
    // after this point is completely unaffected — this only decides which
    // step the CSR begins on.
    const personalFirst = stepOrderQuery.data?.node_order[0];
    if (personalFirst) {
      const personalFirstNode = graphQuery.data.nodes.find((n) => n.id === personalFirst);
      const hasOutgoing = graphQuery.data.edges.some((e) => e.source_node === personalFirst);
      if (
        personalFirstNode &&
        personalFirstNode.node_type !== "START" &&
        personalFirstNode.node_type !== "END" &&
        hasOutgoing
      ) {
        return personalFirst;
      }
    }
    return scriptDefault;
  }, [startNode, graphQuery.data, stepOrderQuery.data]);

  // Hydrate the player at the flow's real first node once the graph loads
  // — deliberately doesn't wait on stepOrderQuery (see the loading guard
  // below, which only waits on graphQuery): stepOrderQuery is a
  // best-effort, rarely-configured personalization lookup with no request
  // timeout anywhere in this app, so gating the entire call screen on it
  // risks a CSR stuck on a loading skeleton forever if it ever hangs, for
  // a script that's otherwise perfectly ready to run. If it's still
  // loading at the moment of this first hydration, `firstRealNodeId`
  // simply falls back to the script's own default (see above) — and this
  // effect corrects that guess, silently and exactly once, if a
  // personalized starting step arrives afterward. `autoHydratedToRef`
  // guards that correction: it only ever applies while `currentNodeId`
  // still equals what this effect itself set — the instant the CSR takes
  // any real action (answers a branch, jumps via "My step order", hits
  // Start Over), `currentNodeId` moves away from that value and this
  // effect goes permanently inert, so a personalization arriving late can
  // never yank a CSR already mid-call back to a different step.
  const autoHydratedToRef = useRef<string | null>(null);
  useEffect(() => {
    if (!firstRealNodeId) return;
    if (currentNodeId === null) {
      setCurrentNodeId(firstRealNodeId);
      autoHydratedToRef.current = firstRealNodeId;
      return;
    }
    if (
      autoHydratedToRef.current !== null &&
      currentNodeId === autoHydratedToRef.current &&
      firstRealNodeId !== autoHydratedToRef.current &&
      !stepOrderQuery.isLoading
    ) {
      setCurrentNodeId(firstRealNodeId);
      autoHydratedToRef.current = firstRealNodeId;
    }
  }, [firstRealNodeId, currentNodeId, stepOrderQuery.isLoading]);

  // Resets the walkthrough state for a fresh call — used by the
  // auto-complete effect below, handleEndCall, and restart(). Defined
  // here (not after the loading-guard return, where restart/handleEndCall
  // live) specifically so the effect below — which must run unconditionally
  // per rules of hooks — can call it too.
  const resetForNextCall = () => {
    setBreadcrumb([]);
    setVisitedEdges([]);
    setFreeTextEntries([]);
    setClientInfoEntries([]);
    setCurrentNodeId(firstRealNodeId);
    setCallStartedAt(Date.now());
  };

  // Writes one CallLog row — shared by the auto-complete effect
  // (outcome=COMPLETED) and handleEndCall (outcome=ENDED_EARLY) so the
  // payload shape and error handling can't drift between the two. Same
  // rules-of-hooks reason as resetForNextCall above for living here.
  const submitCallLog = (
    outcome: "COMPLETED" | "ENDED_EARLY",
    extra: { end_reason?: CallEndReason; end_reason_note?: string } = {},
    onSuccess?: () => void
  ) => {
    if (!currentNodeId) return;
    createCallLog.mutate(
      {
        outcome,
        started_at: new Date(callStartedAt).toISOString(),
        current_node_id: currentNodeId,
        steps: buildCallLogSteps(breadcrumb, visitedEdges, freeTextEntries, clientInfoEntries),
        ...extra,
      },
      {
        onSuccess,
        onError: (err) =>
          toast.error(apiErrorMessage(err, t("flowPlayer.callLogSaveError"))),
      }
    );
  };

  // Computed ahead of the loading/error early-return below so these hooks
  // still fire on every render (rules of hooks) — `nodesById`/`currentNode`
  // get recomputed for rendering after the early return, from the same
  // source, once the graph has actually loaded.
  const isEndNode =
    graphQuery.data?.nodes.find((n) => n.id === currentNodeId)?.node_type === "END";

  // Compiles this call's captured answers into a CallSummarySnapshot —
  // shared by the auto-complete effect below (reaching the flow's real END
  // node) and handleCompleteCall (Staff finished helping the caller and
  // ended the call themselves, without walking the rest of the script).
  // `outcomeLabel` is the human-readable text shown in the summary's
  // Outcome row: the END node's own title for the automatic path, a fixed
  // "Completed" label for the manual one. Defined here (not after the
  // loading-guard return, where handleCompleteCall lives) for the same
  // rules-of-hooks reason as resetForNextCall/submitCallLog above.
  const buildCallSummary = (outcomeLabel: string): CallSummarySnapshot | null => {
    if (!graphQuery.data) return null;
    const nodesMap = new Map(graphQuery.data.nodes.map((n) => [n.id, n]));
    // Client Information steps are left out of `entries` — see
    // CallSummarySnapshot.clientInfo and ClientInfoSummary — so the
    // caller's personal details land in their own section instead of
    // sitting inline with routine branch choices.
    const entries = breadcrumb.flatMap((nodeId, i) => {
      const node = nodesMap.get(nodeId);
      if (node?.builder_step_type === "client_info") return [];
      const edge = visitedEdges[i];
      const freeText = freeTextEntries[i];
      const question = node
        ? resolveBilingual(languagePreference, { en: node.title, es: node.title_es }).primary
        : "";
      // A Text Entry step's answer is whatever the CSR typed, not the
      // edge's static "Continue" label.
      const answer =
        freeText != null
          ? freeText
          : edge
            ? resolveBilingual(languagePreference, {
                en: edge.label || t("flowPlayer.continueLabel"),
                es: edge.label_es,
              }).primary
            : "";
      return [{ question, answer }];
    });
    // If a Client Information step somehow got visited more than once in
    // one call (a loop back to it, or a script with more than one such
    // step), take the LAST non-null entry — matching FlowViewSet.call_log,
    // which does the same "last visit wins" resolution server-side. Taking
    // the first here would show the CSR a different answer locally than
    // what actually gets persisted to CallLogClientInfo.
    let clientInfo: ClientInfoValue | null = null;
    for (const c of clientInfoEntries) {
      if (c != null) clientInfo = c;
    }

    return {
      flowTitle: resolveBilingual(languagePreference, {
        en: graphQuery.data.flow.title,
        es: graphQuery.data.flow.title_es,
      }).primary,
      completedAt: Date.now(),
      entries,
      clientInfo,
      outcome: outcomeLabel,
    };
  };

  // The instant the player lands on the flow's END node: compile this
  // call's answers into `lastCallSummary`, then reset straight back to the
  // first step — no waiting screen. A CSR taking dozens of calls a day
  // can't afford a forced pause after every one; the summary card below
  // stays up (copy/download at their own pace) without blocking the next
  // call. `useLayoutEffect` so the reset happens before the browser paints
  // the END state, avoiding a visible flash of it.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reads
  // breadcrumb/visitedEdges/freeTextEntries/clientInfoEntries/currentNodeId/
  // graphQuery.data/firstRealNodeId as of the render where isEndNode just
  // became true; re-running on every change to those would fire this
  // mid-call instead of only at the end.
  useLayoutEffect(() => {
    if (!isEndNode || !graphQuery.data || !firstRealNodeId || !currentNodeId) return;

    const endNode = graphQuery.data.nodes.find((n) => n.id === currentNodeId);
    const summary = buildCallSummary(
      endNode
        ? resolveBilingual(languagePreference, { en: endNode.title, es: endNode.title_es }).primary
        : ""
    );
    if (summary) setLastCallSummary(summary);

    // Persist for auditing — fire-and-forget, no onSuccess toast (this
    // fires automatically dozens of times a day; a success toast would
    // just be noise), but still surfaces a failure via submitCallLog's
    // shared onError. Only ids go over the wire (see CallLogStepInput) —
    // the server resolves the actual question/answer text from the real
    // node/edge rows itself, not whatever the client sends.
    submitCallLog("COMPLETED");
    resetForNextCall();
  }, [isEndNode]);

  // Keyboard-only navigation for running a script hands-off-mouse mid-call:
  // Enter/Space picks the branch when there's only one way forward
  // ("Continue"), 1-9 picks among multiple branches at a decision point
  // (same order as rendered — see the KeyHint badges below), Backspace
  // goes back a step. Suppressed while focus is in a text field (the
  // personalization editor, Guides search) so typing isn't hijacked.
  // Self-contained (doesn't call choose/goBack/restart below, which are
  // defined after the loading-guard return and so out of scope for a hook
  // that — per rules of hooks — must sit before any conditional return).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!graphQuery.data) return;
      const target = e.target as HTMLElement | null;
      const isEditable =
        target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
      if (isEditable) return;

      const current = currentNodeId
        ? graphQuery.data.nodes.find((n) => n.id === currentNodeId)
        : undefined;
      if (!current) return;

      if (current.node_type === "END") {
        if (e.key === "Enter") {
          e.preventDefault();
          setBreadcrumb([]);
          setVisitedEdges([]);
          setFreeTextEntries([]);
          setClientInfoEntries([]);
          setCurrentNodeId(firstRealNodeId);
        }
        return;
      }

      if (e.key === "Backspace") {
        setBreadcrumb((b) => {
          if (b.length === 0) return b;
          e.preventDefault();
          const prev = b[b.length - 1];
          setCurrentNodeId(prev);
          setVisitedEdges((v) => v.slice(0, -1));
          setFreeTextEntries((f) => f.slice(0, -1));
          setClientInfoEntries((c) => c.slice(0, -1));
          return b.slice(0, -1);
        });
        return;
      }

      // A Text Entry or Client Information step requires the CSR to
      // actually fill something in — there's no single-edge "press Enter
      // to continue" shortcut for either, same reasoning the visible
      // Continue button uses (see FreeTextAnswer/ClientInfoAnswer below).
      // They have to use the input(s).
      if (current.builder_step_type === "text_entry" || current.builder_step_type === "client_info")
        return;

      const sorted = graphQuery.data.edges
        .filter((ed) => ed.source_node === current.id)
        .sort((a, b) => a.sort_order - b.sort_order);
      // Same personal order the visible buttons use (see the branch
      // rendering block below) — otherwise pressing "1" could jump to a
      // branch other than the one actually showing as option 1.
      const outgoing = applyPersonalOrder(sorted, personalizationQuery.data?.[current.id]?.edge_order);

      let chosen: FlowEdge | undefined;
      if (outgoing.length === 1 && (e.key === "Enter" || e.key === " ")) {
        chosen = outgoing[0];
      } else if (outgoing.length > 1 && /^[1-9]$/.test(e.key)) {
        chosen = outgoing[Number(e.key) - 1];
      }
      if (chosen) {
        e.preventDefault();
        setBreadcrumb((b) => [...b, current.id]);
        setVisitedEdges((v) => [...v, chosen!]);
        setFreeTextEntries((f) => [...f, null]);
        setClientInfoEntries((c) => [...c, null]);
        setCurrentNodeId(chosen!.target_node);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [graphQuery.data, currentNodeId, firstRealNodeId, personalizationQuery.data]);

  // Everything below derives from graphQuery.data + currentNodeId, computed
  // ahead of the loading-guard return (with safe fallbacks) so the drag-
  // reorder hook right after it — which, per rules of hooks, must be
  // called unconditionally on every render — always has a real (if
  // possibly empty) `orderedEdges` to work with.
  const nodesById = useMemo(
    () => new Map((graphQuery.data?.nodes ?? []).map((n) => [n.id, n])),
    [graphQuery.data]
  );
  // Every jumpable/orderable step for StepOrderDialog — START/END excluded
  // (see StaffFlowStepOrder), and must have somewhere to go: an admin can
  // edit a published flow's graph without republishing (see
  // validate_flow_for_publish, which only re-checks reachability at
  // publish time), so a node with no outgoing edges can exist here even
  // though it never could have when the flow was first published — jumping
  // or starting a call on one would otherwise strand the CSR immediately
  // on "no branch configured." Sorted by the admin's own authored order as
  // the fallback the dialog applies a personal order on top of.
  const orderableNodes = useMemo(() => {
    if (!graphQuery.data) return [];
    const { nodes, edges } = graphQuery.data;
    return [...nodes]
      .filter((n) => n.node_type !== "START" && n.node_type !== "END")
      .filter((n) => edges.some((e) => e.source_node === n.id))
      .sort((a, b) => a.position_y - b.position_y);
  }, [graphQuery.data]);
  const currentNode: FlowNode | undefined = currentNodeId ? nodesById.get(currentNodeId) : undefined;
  const outgoingEdges: FlowEdge[] = useMemo(() => {
    if (!currentNode || !graphQuery.data) return [];
    return graphQuery.data.edges
      .filter((e) => e.source_node === currentNode.id)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [currentNode, graphQuery.data]);
  // This Staff member's own personalization for the current step — their
  // own wording (see PersonalizedSpiel) and/or their own branch order (see
  // orderedEdges/applyPersonalOrder below). `undefined` when they've never
  // personalized this particular step.
  const override = currentNode ? personalizationQuery.data?.[currentNode.id] : undefined;
  const orderedEdges = useMemo(
    () => applyPersonalOrder(outgoingEdges, override?.edge_order),
    [outgoingEdges, override]
  );
  const hasPersonalOrder = !!override?.edge_order.length;

  // Drag-to-reorder for the current step's branch buttons — see
  // useDragReorder. `disabled` while a save is in flight closes a race a
  // second overlapping drag/click could otherwise win: without it, two
  // saves fired close together could land out of order and the later one
  // — computed from the still-stale pre-save order — would silently
  // overwrite the earlier, actually-intended reorder.
  const edgeReorder = useDragReorder<FlowEdge>({
    items: orderedEdges,
    onCommit: (newOrder) => {
      if (!currentNode) return;
      savePersonalization.mutate(
        { node_id: currentNode.id, edge_order: newOrder },
        { onError: (err) => toast.error(apiErrorMessage(err, t("flowPlayer.personalizationSaveError"))) }
      );
    },
    disabled: savePersonalization.isPending,
  });
  // Cleared whenever the step changes so a stale drag from the previous
  // step's buttons can never linger onto this one's.
  useEffect(() => {
    edgeReorder.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only
    // when the step itself changes, not on every render (edgeReorder is a
    // fresh object each render, so it can't safely be a dependency here).
  }, [currentNodeId]);

  // Deliberately doesn't also wait on stepOrderQuery — see the hydration
  // effect above for why the call screen must never block on it.
  if (graphQuery.isLoading || !graphQuery.data) {
    if (graphQuery.isError) {
      const notPublished = isAxiosError(graphQuery.error) && graphQuery.error.response?.status === 403;
      return (
        <AppShell>
          <div className="mx-auto max-w-md py-16 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-danger" aria-hidden="true" />
            <h1 className="mt-3 text-lg font-semibold text-slate-900">
              {notPublished ? t("flowPlayer.notAvailableTitle") : t("flowPlayer.loadErrorTitle")}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {notPublished
                ? t("flowPlayer.notAvailableDescription")
                : t("flowPlayer.loadErrorDescription")}
            </p>
            <Button className="mt-4" asChild>
              <Link to="/">{t("flowPlayer.backToFlows")}</Link>
            </Button>
          </div>
        </AppShell>
      );
    }
    return <PageSkeleton />;
  }

  const { flow } = graphQuery.data;
  const isEnd = currentNode?.node_type === "END";

  const flowTitle = resolveBilingual(languagePreference, { en: flow.title, es: flow.title_es });
  const internalNote = currentNode
    ? resolveBilingual(languagePreference, {
        en: currentNode.internal_note,
        es: currentNode.internal_note_es,
      })
    : null;

  // What's been captured so far in the call *in progress* — recomputed
  // from buildCallSummary on every render (cheap, plain state reads, no
  // memoization needed), so it's already showing the answer the instant
  // Staff submits one, before they move to the next step. Distinct from
  // lastCallSummary, which is a frozen snapshot of the *previous* call
  // that stays up after this one starts. `outcome` isn't used here — this
  // call hasn't ended yet — so it's passed empty. `clientInfoEntries` is
  // always the same length as `breadcrumb` (every setter above pushes/
  // slices them together), so checking breadcrumb alone is enough to know
  // whether either one has anything in it.
  const inProgressSummary = breadcrumb.length > 0 ? buildCallSummary("") : null;

  // `freeText`/`clientInfo` are only passed when advancing off a Text
  // Entry step (see FreeTextAnswer below) or a Client Information step
  // (see ClientInfoAnswer) respectively — every ordinary button click
  // leaves both `null`.
  const choose = (
    edge: FlowEdge,
    freeText: string | null = null,
    clientInfo: ClientInfoValue | null = null
  ) => {
    if (!currentNodeId) return;
    setBreadcrumb((b) => [...b, currentNodeId]);
    setVisitedEdges((v) => [...v, edge]);
    setFreeTextEntries((f) => [...f, freeText]);
    setClientInfoEntries((c) => [...c, clientInfo]);
    setCurrentNodeId(edge.target_node);
  };

  const goBack = () => {
    setBreadcrumb((b) => {
      if (b.length === 0) return b;
      const prev = b[b.length - 1];
      setCurrentNodeId(prev);
      setVisitedEdges((v) => v.slice(0, -1));
      setFreeTextEntries((f) => f.slice(0, -1));
      setClientInfoEntries((c) => c.slice(0, -1));
      return b.slice(0, -1);
    });
  };

  const jumpTo = (index: number) => {
    const targetId = breadcrumb[index];
    setBreadcrumb((b) => b.slice(0, index));
    setVisitedEdges((v) => v.slice(0, index));
    setFreeTextEntries((f) => f.slice(0, index));
    setClientInfoEntries((c) => c.slice(0, index));
    setCurrentNodeId(targetId);
  };

  // Deliberately does NOT write a CallLog — "Start over" means "I want to
  // redo this same still-ongoing call from the top" (wrong branch picked,
  // want to re-read the intro, etc.), not "this call is over." Logging it
  // here would conflate routine in-script navigation with an actual call
  // ending, polluting the audit trail with an entry every time Staff use
  // this. Only reaching END or clicking "End call" represents a real
  // outcome worth auditing.
  const restart = () => {
    resetForNextCall();
  };

  const handleEndCall = ({ reason, note }: { reason: Exclude<CallEndReason, "">; note: string }) => {
    if (!currentNodeId) return;
    submitCallLog("ENDED_EARLY", { end_reason: reason, end_reason_note: note }, () =>
      toast.success(t("flowPlayer.callLogSaved"))
    );
    // Reset regardless of whether the log write lands — getting Staff
    // back to a fresh script for the next call matters more than waiting
    // on that request, and a failed write shouldn't trap them on a dead
    // call (submitCallLog's onError still tells them if it didn't save).
    setEndCallDialogOpen(false);
    resetForNextCall();
  };

  // For a call that was genuinely handled — the caller's need was resolved
  // — but that wraps up somewhere other than the script's own END node (a
  // short call, an off-script resolution, whatever). Distinct from
  // handleEndCall: this logs outcome=COMPLETED, not ENDED_EARLY, and skips
  // the reason dialog entirely since "why did this end" doesn't apply to a
  // call that finished the way it was supposed to. Same immediate-reset,
  // no-confirmation pattern as restart() — nothing here needs a second
  // click to be safe, since the answers already captured stay on the
  // record either way.
  const handleCompleteCall = () => {
    if (!currentNodeId) return;
    const summary = buildCallSummary(t("flowPlayer.completeCallOutcome"));
    if (summary) setLastCallSummary(summary);
    submitCallLog("COMPLETED", {}, () => toast.success(t("flowPlayer.callCompletedLogged")));
    resetForNextCall();
  };

  const buildSummaryText = (summary: CallSummarySnapshot) => {
    const lines = [
      `${t("flowPlayer.summaryHeading")}: ${summary.flowTitle}`,
      `${t("flowPlayer.summaryTimestamp")}: ${new Date(summary.completedAt).toLocaleString()}`,
      "",
    ];
    if (summary.entries.length === 0) {
      lines.push(t("flowPlayer.summaryNoSteps"));
    } else {
      summary.entries.forEach((entry, i) => {
        lines.push(`${i + 1}. ${entry.question}`);
        lines.push(`   → ${entry.answer}`);
      });
    }
    // Its own section, deliberately separate from the numbered steps above
    // — see CallSummarySnapshot.clientInfo.
    if (summary.clientInfo) {
      const info = summary.clientInfo;
      lines.push("", `${t("flowPlayer.clientInfoHeading")}:`);
      lines.push(`  ${t("flowPlayer.clientInfoFirstName")}: ${info.first_name || "—"}`);
      lines.push(`  ${t("flowPlayer.clientInfoLastName")}: ${info.last_name || "—"}`);
      lines.push(`  ${t("flowPlayer.clientInfoDob")}: ${info.date_of_birth || "—"}`);
      lines.push(`  ${t("flowPlayer.clientInfoPhone")}: ${info.phone_number || "—"}`);
      lines.push(`  ${t("flowPlayer.clientInfoEmail")}: ${info.email || "—"}`);
      if (info.declined_to_share) {
        lines.push(`  (${t("flowPlayer.clientInfoDeclinedBadge")})`);
      }
    }
    lines.push("", `${t("flowPlayer.summaryOutcome")}: ${summary.outcome}`);
    return lines.join("\n");
  };

  const handleCopySummary = async () => {
    if (!lastCallSummary) return;
    try {
      await navigator.clipboard.writeText(buildSummaryText(lastCallSummary));
      toast.success(t("flowPlayer.summaryCopied"));
    } catch {
      toast.error(t("flowPlayer.summaryCopyError"));
    }
  };

  const handleDownloadSummary = () => {
    if (!lastCallSummary) return;
    const blob = new Blob([buildSummaryText(lastCallSummary)], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const slug =
      lastCallSummary.flowTitle
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "") || "call";
    anchor.href = url;
    anchor.download = `${slug}-summary-${lastCallSummary.completedAt}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const handleSavePersonalization = async (input: { spiel_text: string; spiel_text_es: string }) => {
    if (!currentNode) return;
    try {
      await savePersonalization.mutateAsync({ node_id: currentNode.id, ...input });
      toast.success(t("flowPlayer.personalizationSaved"));
    } catch (err) {
      toast.error(apiErrorMessage(err, t("flowPlayer.personalizationSaveError")));
      throw err; // let PersonalizedSpiel know the save failed, so it stays open
    }
  };

  const handleResetPersonalization = () => {
    if (!currentNode) return;
    resetPersonalization.mutate(currentNode.id, {
      onSuccess: () => toast.success(t("flowPlayer.personalizationReset")),
      onError: (err) => toast.error(apiErrorMessage(err, t("flowPlayer.personalizationResetError"))),
    });
  };

  // Reordering the current step's branch buttons — the CSR's own preferred
  // order, never shared (see StaffNodeOverride.edge_order). Dragging and
  // the Up/Down buttons both go through the edgeReorder hook (see above),
  // which PUTs only `edge_order` — the backend merges it in without
  // touching whatever wording this Staff member already personalized (see
  // apps/flows/views.py::FlowViewSet.personalization).

  const handleResetOrder = () => {
    if (!currentNode) return;
    savePersonalization.mutate(
      { node_id: currentNode.id, edge_order: [] },
      {
        onSuccess: () => toast.success(t("flowPlayer.orderReset")),
        onError: (err) => toast.error(apiErrorMessage(err, t("flowPlayer.personalizationResetError"))),
      }
    );
  };

  // "My step order" — see StepOrderDialog. Distinct from edgeReorder/
  // handleResetOrder above (that's one node's own branch order); this is
  // the CSR's own order across the whole flow's steps.
  const handleReorderSteps = (newOrder: string[]) => {
    saveStepOrder.mutate(newOrder, {
      onError: (err) => toast.error(apiErrorMessage(err, t("flowPlayer.stepOrderSaveError"))),
    });
  };

  const handleResetStepOrder = () => {
    resetStepOrder.mutate(undefined, {
      onSuccess: () => toast.success(t("flowPlayer.stepOrderReset")),
      onError: (err) => toast.error(apiErrorMessage(err, t("flowPlayer.stepOrderResetError"))),
    });
  };

  // Jumps straight to another step at the CSR's own request — deliberately
  // doesn't touch breadcrumb/visitedEdges/etc (those drive the call-log
  // transcript, resolved from real traversed edges — see buildCallLogSteps)
  // so a jump isn't misrecorded as if it were a real answered branch. Same
  // "no log entry for this move" treatment as restart() below. The node
  // this lands on always has its own outgoing edges intact (START/END are
  // excluded from step_order — see StaffFlowStepOrder), so the normal
  // button-click flow continues exactly as if the CSR had arrived here any
  // other way.
  const handleJumpToStep = (nodeId: string) => {
    setCurrentNodeId(nodeId);
  };

  const breadcrumbTitle = (nodeId: string) => {
    const n = nodesById.get(nodeId);
    if (!n) return "";
    return resolveBilingual(languagePreference, { en: n.title, es: n.title_es }).primary;
  };

  return (
    <AppShell>
      {/* The script (left) scrolls as usual; Guides & FAQs (right) stays
       * pinned so Staff can look up off-script guidance — a difficult
       * caller, an edge case — without losing their place mid-call. Side
       * by side only from `lg` up; below that it falls beneath the script,
       * same as the flow builder's live-preview layout. */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="mx-auto w-full max-w-3xl">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h1 className="m-0 text-2xl font-bold text-slate-900">{flowTitle.primary}</h1>
              {flowTitle.secondary && (
                <p className="m-0 text-sm text-slate-500">{flowTitle.secondary}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {!isEnd && (
                <Button variant="secondary" size="sm" onClick={handleCompleteCall}>
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  {t("flowPlayer.completeCall")}
                </Button>
              )}
              {!isEnd && (
                <Button variant="secondary" size="sm" onClick={() => setEndCallDialogOpen(true)}>
                  <PhoneOff className="h-4 w-4" aria-hidden="true" />
                  {t("flowPlayer.endCall")}
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={restart}>
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                {t("flowPlayer.startOver")}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setStepOrderDialogOpen(true)}>
                <ListOrdered className="h-4 w-4" aria-hidden="true" />
                {t("flowPlayer.stepOrderButton")}
              </Button>
            </div>
          </div>

          {/* Last call's compiled answers — the flow already reset itself
           * to the first step the instant it hit END (see the layout
           * effect above), so this sits alongside the new call in
           * progress rather than blocking it. Stays up until dismissed or
           * replaced by the next call's summary. */}
          {lastCallSummary && (
            <div className="app-card mt-4 p-6">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h2 className="m-0 text-base font-semibold text-slate-900">
                    {t("flowPlayer.summaryHeading")}
                  </h2>
                  <p className="m-0 text-xs text-slate-500">
                    {new Date(lastCallSummary.completedAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={handleCopySummary}>
                    <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("flowPlayer.copySummary")}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleDownloadSummary}>
                    <Download className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("flowPlayer.downloadSummary")}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setLastCallSummary(null)}
                    aria-label={t("flowPlayer.dismissSummary")}
                    className="cursor-pointer rounded p-1.5 text-slate-400 hover:bg-surface-alt hover:text-slate-600"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <SummaryEntries
                entries={lastCallSummary.entries}
                clientInfo={lastCallSummary.clientInfo}
                emptyMessage={t("flowPlayer.summaryNoSteps")}
              />

              <div className="mt-3 border-t border-slate-200 pt-3 text-sm">
                <span className="font-medium text-slate-900">{t("flowPlayer.summaryOutcome")}: </span>
                <span className="text-slate-600">{lastCallSummary.outcome}</span>
              </div>
            </div>
          )}

          {/* Breadcrumb trail — each chip jumps back to that step. */}
          {breadcrumb.length > 0 && (
            <nav
              aria-label={t("flowPlayer.stepsTaken")}
              className="mt-4 flex flex-wrap items-center gap-1 text-sm"
            >
              {breadcrumb.map((nodeId, i) => (
                <span key={nodeId} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => jumpTo(i)}
                    className="cursor-pointer rounded px-1.5 py-0.5 text-slate-500 underline-offset-2 hover:bg-surface-alt hover:text-slate-900 hover:underline"
                  >
                    {breadcrumbTitle(nodeId)}
                  </button>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />
                </span>
              ))}
              <span className="px-1.5 py-0.5 font-medium text-slate-900">
                {currentNode && breadcrumbTitle(currentNode.id)}
              </span>
            </nav>
          )}

          {/* Live call notes, below `lg` only — from `lg` up this same data
           * renders in the sidebar instead (see LiveNotes below), pinned
           * next to Guides & FAQs; below that breakpoint the sidebar is
           * hidden entirely, so this is the only place Staff on a
           * phone/tablet ever see what's been captured so far. */}
          {inProgressSummary && (
            <div className="mt-4 lg:hidden">
              <LiveNotes entries={inProgressSummary.entries} clientInfo={inProgressSummary.clientInfo} />
            </div>
          )}

          {/* The spiel — the single most important element on this page. */}
          <div className="app-card mt-4 p-8">
            {isEnd ? (
              <div className="text-center">
                <StatusPill kind="success">{t("flowPlayer.flowComplete")}</StatusPill>
              </div>
            ) : (
              <div className="text-xs font-semibold uppercase tracking-wide text-primary">
                {currentNode?.builder_step_type === "text_entry"
                  ? t("flowPlayer.textEntryPoint")
                  : currentNode?.builder_step_type === "client_info"
                    ? t("flowPlayer.clientInfoPoint")
                    : currentNode?.node_type === "DECISION"
                      ? t("flowPlayer.decisionPoint")
                      : t("flowPlayer.sayThis")}
              </div>
            )}

            {currentNode && (
              <PersonalizedSpiel
                key={currentNode.id}
                node={currentNode}
                override={override}
                languagePreference={languagePreference}
                onSave={handleSavePersonalization}
                onReset={handleResetPersonalization}
                saving={savePersonalization.isPending}
                resetting={resetPersonalization.isPending}
              />
            )}

            {internalNote && internalNote.primary && (
              <div className="mt-5 flex gap-2 rounded-md border border-warning/30 bg-warning-subtle p-3 text-sm text-slate-800">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
                <div className="max-w-prose">
                  <div className="font-medium text-warning">{t("flowPlayer.internalNoteLabel")}</div>
                  <BilingualBlock text={internalNote} className="mt-0.5 leading-relaxed" />
                </div>
              </div>
            )}
          </div>

          {/* Branches — or, for a Text Entry step, a free-text box, or for
           * a Client Information step, the name/DOB/email/phone form,
           * instead of predefined buttons. Such a step always has exactly
           * one outgoing edge (auto-connected by the builder, same as any
           * other non-branching step); what actually gets logged as the
           * answer is whatever the CSR typed/entered, not that edge's
           * label. */}
          {!isEnd && currentNode?.builder_step_type === "text_entry" && (
            <div className="mt-4">
              {outgoingEdges[0] ? (
                <FreeTextAnswer
                  key={currentNode.id}
                  onSubmit={(text) => choose(outgoingEdges[0], text)}
                />
              ) : (
                <p className="text-sm text-danger">{t("flowPlayer.noBranchConfigured")}</p>
              )}
            </div>
          )}

          {!isEnd && currentNode?.builder_step_type === "client_info" && (
            <div className="mt-4">
              {outgoingEdges[0] ? (
                <ClientInfoAnswer
                  key={currentNode.id}
                  onSubmit={(value) => choose(outgoingEdges[0], null, value)}
                />
              ) : (
                <p className="text-sm text-danger">{t("flowPlayer.noBranchConfigured")}</p>
              )}
            </div>
          )}

          {!isEnd &&
            currentNode?.builder_step_type !== "text_entry" &&
            currentNode?.builder_step_type !== "client_info" && (
            <div className="mt-4 space-y-2">
              {orderedEdges.length === 0 ? (
                <p className="text-sm text-danger">{t("flowPlayer.noBranchConfigured")}</p>
              ) : (
                <>
                  {/* Drag-to-reorder is a CSR's own private preference —
                   * see StaffNodeOverride.edge_order — never visible to
                   * anyone else and never changes which step each option
                   * actually leads to. Only worth explaining when there's
                   * more than one button to reorder. */}
                  {orderedEdges.length > 1 && (
                    <div className="flex flex-wrap items-center gap-2 pb-1 text-xs text-slate-500">
                      <GripVertical className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span>{t("flowPlayer.reorderHint")}</span>
                      {hasPersonalOrder && (
                        <>
                          <Badge tone="info">{t("flowPlayer.personalizedOrderBadge")}</Badge>
                          <button
                            type="button"
                            onClick={handleResetOrder}
                            disabled={savePersonalization.isPending}
                            className="cursor-pointer font-medium text-slate-500 hover:text-danger hover:underline disabled:pointer-events-none disabled:opacity-50"
                          >
                            {t("flowPlayer.resetOrder")}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {orderedEdges.map((edge, i) => {
                    const label = resolveBilingual(languagePreference, {
                      en: edge.label || t("flowPlayer.continueLabel"),
                      es: edge.label_es,
                    });
                    const row = edgeReorder.dragRowProps(edge.id);
                    return (
                      <div
                        key={edge.id}
                        draggable={row.draggable}
                        onDragStart={row.onDragStart}
                        onDragOver={row.onDragOver}
                        onDragLeave={row.onDragLeave}
                        onDrop={row.onDrop}
                        onDragEnd={row.onDragEnd}
                        className={cn(
                          "flex items-center gap-1 rounded-md",
                          row.draggable && "cursor-grab active:cursor-grabbing",
                          row.isDragging && "opacity-40",
                          row.isDragOver && "ring-2 ring-primary"
                        )}
                      >
                        {orderedEdges.length > 1 && (
                          // Structural — shown whenever there's more than
                          // one option to reorder, regardless of whether a
                          // save happens to be in flight right now (that
                          // only disables the buttons below, via
                          // savePersonalization.isPending, same as it
                          // already did — `edgeReorder.canReorder` also
                          // factors in that same disabled state, so using
                          // it here instead would make the whole block
                          // blink out mid-save rather than just gray out).
                          // Mouse users can drag the row itself; these
                          // buttons are the only way to reorder on a touch
                          // device or via keyboard, since native HTML5
                          // drag-and-drop supports neither.
                          <div className="flex shrink-0 flex-col items-center">
                            <button
                              type="button"
                              onClick={() => edgeReorder.moveByOffset(edge.id, -1)}
                              disabled={i === 0 || savePersonalization.isPending}
                              aria-label={t("flowPlayer.moveUp")}
                              className="cursor-pointer rounded p-0.5 text-slate-400 hover:text-primary disabled:pointer-events-none disabled:opacity-30"
                            >
                              <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                            <GripVertical
                              className="h-3.5 w-3.5 text-slate-300"
                              aria-hidden="true"
                            />
                            <button
                              type="button"
                              onClick={() => edgeReorder.moveByOffset(edge.id, 1)}
                              disabled={i === orderedEdges.length - 1 || savePersonalization.isPending}
                              aria-label={t("flowPlayer.moveDown")}
                              className="cursor-pointer rounded p-0.5 text-slate-400 hover:text-primary disabled:pointer-events-none disabled:opacity-30"
                            >
                              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => choose(edge)}
                          className="app-focus-ring flex w-full cursor-pointer items-center justify-between rounded-md border border-slate-300 bg-surface px-4 py-3 text-left text-base font-medium text-slate-900 transition-colors duration-150 hover:border-primary hover:bg-primary-subtle"
                        >
                          <span>
                            {label.primary}
                            {label.secondary && (
                              <span className="block text-sm font-normal text-slate-500">
                                {label.secondary}
                              </span>
                            )}
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            {orderedEdges.length === 1 && <KeyHint>↵</KeyHint>}
                            {orderedEdges.length > 1 && i < 9 && <KeyHint>{i + 1}</KeyHint>}
                            <ChevronRight className="h-4 w-4 text-slate-400" aria-hidden="true" />
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* Reached only if the flow has no first step to reset to (a
           * misconfigured script with no START) — the normal case never
           * shows this, since the layout effect above resets instantly. */}
          {isEnd && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={restart}>
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                {t("flowPlayer.startOver")}
              </Button>
              <Button variant="secondary" onClick={() => navigate("/")}>
                {t("flowPlayer.backToFlows")}
              </Button>
            </div>
          )}

          {breadcrumb.length > 0 && !isEnd && (
            <button
              type="button"
              onClick={goBack}
              className="mt-4 flex cursor-pointer items-center gap-1.5 text-sm text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline"
            >
              ← {t("flowPlayer.back")}
              <KeyHint>⌫</KeyHint>
            </button>
          )}
        </div>

        {/* Sidebar (from `lg` up only, same as GuidePanel always was): the
         * live call notes (when there's anything captured yet) sit above
         * Guides & FAQs, sized to their own content, so neither steals the
         * other's space — Guides keeps the rest of the column and its own
         * internal scroll, same as before this was added. Below `lg` this
         * whole column is hidden, so the notes render a second time inline
         * in the main column instead — see the `lg:hidden` block above. */}
        <div className="sticky top-[5.5rem] hidden h-[calc(100vh-7.5rem)] flex-col gap-4 lg:flex">
          {inProgressSummary && (
            <LiveNotes entries={inProgressSummary.entries} clientInfo={inProgressSummary.clientInfo} />
          )}
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-surface shadow-card">
            <GuidePanel />
          </div>
        </div>
      </div>

      <EndCallDialog
        open={endCallDialogOpen}
        onClose={() => setEndCallDialogOpen(false)}
        onConfirm={handleEndCall}
        submitting={createCallLog.isPending}
      />

      <StepOrderDialog
        open={stepOrderDialogOpen}
        onClose={() => setStepOrderDialogOpen(false)}
        nodes={orderableNodes}
        order={stepOrderQuery.data?.node_order ?? []}
        onReorder={handleReorderSteps}
        onReset={handleResetStepOrder}
        onJumpTo={handleJumpToStep}
        saving={saveStepOrder.isPending}
        languagePreference={languagePreference}
      />
    </AppShell>
  );
}
